import { supabase } from '../lib/supabase';
import type {
  Product,
  ProductWithMedia,
  ProductFull,
  ProductMedia,
  BundleItem,
  ProductRelation,
  UserProductPrice,
  CreateUserProductPriceInput,
} from '../types/products';

// =============================================
// Filter & Pagination
// =============================================

export interface ProductsFilter {
  search?: string;
  category?: string;
  productType?: string;
  isActive?: boolean;
}

export interface PaginationParams {
  page: number;
  pageSize: number;
}

export interface ProductsResponse {
  products: ProductWithMedia[];
  totalCount: number;
}

// =============================================
// Product List Operations
// =============================================

export async function getProducts(
  filters: ProductsFilter = {},
  pagination: PaginationParams = { page: 1, pageSize: 20 }
): Promise<ProductsResponse> {
  const { page, pageSize } = pagination;
  const offset = (page - 1) * pageSize;

  let query = supabase
    .from('products')
    .select('*', { count: 'exact' })
    .eq('is_active', filters.isActive ?? true)
    .order('name', { ascending: true });

  if (filters.search) {
    query = query.or(`name.ilike.%${filters.search}%,sku.ilike.%${filters.search}%`);
  }

  if (filters.category) {
    query = query.eq('primary_category', filters.category);
  }

  if (filters.productType) {
    query = query.eq('product_type', filters.productType);
  }

  query = query.range(offset, offset + pageSize - 1);

  const { data: products, error, count } = await query;

  if (error) throw error;
  if (!products) return { products: [], totalCount: 0 };

  // Fetch media for all products
  const productIds = products.map((p) => p.id);
  const { data: allMedia } = await supabase
    .from('product_media')
    .select('*')
    .in('product_id', productIds)
    .eq('type', 'image')
    .order('sort_order', { ascending: true });

  const mediaByProductId: Record<string, ProductMedia[]> = {};
  allMedia?.forEach((m) => {
    if (!mediaByProductId[m.product_id]) {
      mediaByProductId[m.product_id] = [];
    }
    mediaByProductId[m.product_id].push(m);
  });

  const productsWithMedia: ProductWithMedia[] = products.map((product) => ({
    ...product,
    media: mediaByProductId[product.id] || [],
  }));

  return { products: productsWithMedia, totalCount: count || 0 };
}

export async function getCategories(): Promise<string[]> {
  const { data, error } = await supabase
    .from('products')
    .select('primary_category')
    .eq('is_active', true)
    .not('primary_category', 'is', null);

  if (error) throw error;

  const categories = [...new Set(data?.map((p) => p.primary_category).filter(Boolean))] as string[];
  return categories.sort();
}

// =============================================
// Single Product Operations
// =============================================

export async function getProductBySlug(slug: string): Promise<ProductFull | null> {
  const { data: product, error } = await supabase
    .from('products')
    .select('*')
    .eq('slug', slug)
    .maybeSingle();

  if (error) throw error;
  if (!product) return null;

  // Fetch all related data in parallel
  const [
    mediaResult,
    sectionsResult,
    attributesResult,
    documentsResult,
    bundleItemsResult,
    relationsResult,
  ] = await Promise.all([
    supabase
      .from('product_media')
      .select('*')
      .eq('product_id', product.id)
      .order('sort_order', { ascending: true }),
    supabase
      .from('product_sections')
      .select('*')
      .eq('product_id', product.id)
      .order('sort_order', { ascending: true }),
    supabase
      .from('product_attributes')
      .select('*')
      .eq('product_id', product.id)
      .order('sort_order', { ascending: true }),
    supabase
      .from('product_documents')
      .select('*')
      .eq('product_id', product.id)
      .order('sort_order', { ascending: true }),
    supabase
      .from('bundle_items')
      .select('*')
      .eq('bundle_product_id', product.id)
      .order('sort_order', { ascending: true }),
    supabase
      .from('product_relations')
      .select('*')
      .eq('product_id', product.id)
      .order('sort_order', { ascending: true }),
  ]);

  // Fetch related products for bundle items
  let bundleItems: BundleItem[] = bundleItemsResult.data || [];
  if (bundleItems.length > 0) {
    const childProductIds = bundleItems
      .map((bi) => bi.child_product_id)
      .filter(Boolean) as string[];

    if (childProductIds.length > 0) {
      const { data: childProducts } = await supabase
        .from('products')
        .select('*')
        .in('id', childProductIds);

      const childProductMap: Record<string, Product> = {};
      childProducts?.forEach((cp) => {
        childProductMap[cp.id] = cp;
      });

      bundleItems = bundleItems.map((bi) => ({
        ...bi,
        child_product: bi.child_product_id ? childProductMap[bi.child_product_id] : undefined,
      }));
    }
  }

  // Fetch related products for relations
  let relations: ProductRelation[] = relationsResult.data || [];
  if (relations.length > 0) {
    const relatedProductIds = relations
      .map((r) => r.related_product_id)
      .filter(Boolean) as string[];

    if (relatedProductIds.length > 0) {
      const { data: relatedProducts } = await supabase
        .from('products')
        .select('*')
        .in('id', relatedProductIds);

      const relatedProductMap: Record<string, Product> = {};
      relatedProducts?.forEach((rp) => {
        relatedProductMap[rp.id] = rp;
      });

      relations = relations.map((r) => ({
        ...r,
        related_product: r.related_product_id ? relatedProductMap[r.related_product_id] : undefined,
      }));
    }
  }

  return {
    ...product,
    media: mediaResult.data || [],
    sections: sectionsResult.data || [],
    attributes: attributesResult.data || [],
    documents: documentsResult.data || [],
    bundle_items: bundleItems,
    relations,
  };
}

export async function getProductById(id: string): Promise<Product | null> {
  const { data, error } = await supabase
    .from('products')
    .select('*')
    .eq('id', id)
    .maybeSingle();

  if (error) throw error;
  return data;
}

// =============================================
// Utility Functions
// =============================================

export function formatPrice(price: number | null, currency: string = 'EUR'): string {
  if (price === null) return '-';

  // Normalize currency symbols to ISO codes
  let currencyCode = currency;
  if (currency === '€' || !currency) currencyCode = 'EUR';
  else if (currency === '$') currencyCode = 'USD';
  else if (currency === '£') currencyCode = 'GBP';

  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: currencyCode,
  }).format(price);
}

export function formatRating(rating: number | null): string {
  if (rating === null) return '-';
  return rating.toFixed(1);
}

export function truncateCategoryPath(path: string | null, maxLength: number = 30): string {
  if (!path) return '-';
  if (path.length <= maxLength) return path;

  const parts = path.split(' > ');
  if (parts.length <= 2) return path.substring(0, maxLength) + '...';

  return `${parts[0]} > ... > ${parts[parts.length - 1]}`;
}

// =============================================
// User Product Prices
// =============================================

export async function getUserProductPrice(
  userId: string,
  productId: string
): Promise<UserProductPrice | null> {
  const { data, error } = await supabase
    .from('user_product_prices')
    .select('*')
    .eq('user_id', userId)
    .eq('product_id', productId)
    .maybeSingle();

  if (error) throw error;
  return data;
}

export async function getUserProductPrices(userId: string): Promise<UserProductPrice[]> {
  const { data, error } = await supabase
    .from('user_product_prices')
    .select('*')
    .eq('user_id', userId);

  if (error) throw error;
  return data || [];
}

export async function setUserProductPrice(
  userId: string,
  input: CreateUserProductPriceInput
): Promise<UserProductPrice> {
  // Use upsert to insert or update
  const { data, error } = await supabase
    .from('user_product_prices')
    .upsert({
      user_id: userId,
      product_id: input.product_id,
      price_type: input.price_type,
      custom_price: input.custom_price ?? null,
      updated_at: new Date().toISOString(),
    }, {
      onConflict: 'user_id,product_id',
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function deleteUserProductPrice(
  userId: string,
  productId: string
): Promise<void> {
  const { error } = await supabase
    .from('user_product_prices')
    .delete()
    .eq('user_id', userId)
    .eq('product_id', productId);

  if (error) throw error;
}

// =============================================
// Product Selection for Site Blocks
// =============================================

export interface ProductsByCategory {
  category: string;
  products: ProductWithMedia[];
}

/**
 * Get all active products grouped by category with media.
 * Used for the product selector in the site block editor.
 */
export async function getAllProductsByCategory(): Promise<ProductsByCategory[]> {
  const { data, error } = await supabase
    .from('products')
    .select('*')
    .eq('is_active', true)
    .order('sort_order', { ascending: true })
    .order('name', { ascending: true });

  if (error) throw error;
  if (!data) return [];

  // Fetch media for all products
  const productIds = data.map((p) => p.id);
  const { data: allMedia } = await supabase
    .from('product_media')
    .select('*')
    .in('product_id', productIds)
    .eq('type', 'image')
    .order('sort_order', { ascending: true });

  const mediaByProductId: Record<string, ProductMedia[]> = {};
  allMedia?.forEach((m) => {
    if (!mediaByProductId[m.product_id]) {
      mediaByProductId[m.product_id] = [];
    }
    mediaByProductId[m.product_id].push(m);
  });

  // Create products with media
  const productsWithMedia: ProductWithMedia[] = data.map((product) => ({
    ...product,
    media: mediaByProductId[product.id] || [],
  }));

  // Group by category
  const categoryMap: Record<string, ProductWithMedia[]> = {};
  productsWithMedia.forEach((product) => {
    const category = product.primary_category || 'Sonstige';
    if (!categoryMap[category]) {
      categoryMap[category] = [];
    }
    categoryMap[category].push(product);
  });

  // Convert to array and sort categories
  const categoryOrder = [
    'Getränke',
    'Bienenprodukte',
    'Nahrungsergänzung',
    'Gewichtsmanagement',
    'Programme',
    'Körperpflege',
    'Hautpflege',
    'Haarpflege',
    'Sonstige',
  ];

  const result: ProductsByCategory[] = [];

  // First add categories in order
  categoryOrder.forEach((cat) => {
    if (categoryMap[cat]) {
      result.push({ category: cat, products: categoryMap[cat] });
      delete categoryMap[cat];
    }
  });

  // Then add any remaining categories
  Object.entries(categoryMap).forEach(([category, products]) => {
    result.push({ category, products });
  });

  return result;
}

/**
 * Get multiple products by their IDs with their media.
 * Used to fetch product data for selected products in a block.
 */
export async function getProductsByIds(ids: string[]): Promise<ProductWithMedia[]> {
  if (ids.length === 0) return [];

  const { data: products, error } = await supabase
    .from('products')
    .select('*')
    .in('id', ids);

  if (error) throw error;
  if (!products) return [];

  // Fetch media for all products
  const { data: allMedia } = await supabase
    .from('product_media')
    .select('*')
    .in('product_id', ids)
    .eq('type', 'image')
    .order('sort_order', { ascending: true });

  const mediaByProductId: Record<string, ProductMedia[]> = {};
  allMedia?.forEach((m) => {
    if (!mediaByProductId[m.product_id]) {
      mediaByProductId[m.product_id] = [];
    }
    mediaByProductId[m.product_id].push(m);
  });

  const productsWithMedia: ProductWithMedia[] = products.map((product) => ({
    ...product,
    media: mediaByProductId[product.id] || [],
  }));

  return productsWithMedia;
}
